import Control from "../../Control";
import TextField from "./TextField";
import FileUpload from "../FileUpload/FileUpload";
import {ServerAPI} from "../../ServerAPI";
import post = ServerAPI.post;
import Delegate from "../../Delegate";

/** Replace last extension (or append) with webp. */
function withWebpExtension(name: string): string
{
	const base = name.replace(/\.[^./\\]+$/, '');
	return `${base || 'image'}.webp`;
}

/**
 * Raster → WebP in the browser before S3 PUT.
 * GIF/SVG stay as-is; already-WebP kept; encode failure → original.
 */
async function prepareImageForUpload(file: File): Promise<{ blob: Blob; name: string }>
{
	const type = (file.type || '').toLowerCase();
	if (!type.startsWith('image/') || type === 'image/svg+xml' || type === 'image/gif')
		return { blob: file, name: file.name };
	if (type === 'image/webp')
		return { blob: file, name: withWebpExtension(file.name) };

	try
	{
		const blob = await encodeAsWebp(file, 0.85);
		if (!blob || blob.size === 0 || blob.type !== 'image/webp')
			return { blob: file, name: file.name };
		// Keep JPEG when WebP somehow larger (rare).
		if (blob.size >= file.size && (type === 'image/jpeg' || type === 'image/jpg'))
			return { blob: file, name: file.name };
		return { blob, name: withWebpExtension(file.name) };
	}
	catch
	{
		return { blob: file, name: file.name };
	}
}

function encodeAsWebp(file: File, quality: number): Promise<Blob | null>
{
	return new Promise((resolve, reject) =>
	{
		const url = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () =>
		{
			try
			{
				const canvas = document.createElement('canvas');
				canvas.width = img.naturalWidth || img.width;
				canvas.height = img.naturalHeight || img.height;
				const ctx = canvas.getContext('2d');
				if (!ctx || !canvas.width || !canvas.height)
				{
					URL.revokeObjectURL(url);
					resolve(null);
					return;
				}
				ctx.drawImage(img, 0, 0);
				URL.revokeObjectURL(url);
				if (typeof canvas.toBlob !== 'function')
				{
					resolve(null);
					return;
				}
				canvas.toBlob((b) => resolve(b), 'image/webp', quality);
			}
			catch (e)
			{
				URL.revokeObjectURL(url);
				reject(e);
			}
		};
		img.onerror = () =>
		{
			URL.revokeObjectURL(url);
			reject(new Error('image decode failed'));
		};
		img.src = url;
	});
}

export default class ImageTextField extends Control {
	private _TextFieldSettings: object;
	private _textField: TextField;
	private _uploadButton: FileUpload;
	private EvChanged: Delegate;
	private _UploadApiPath: string;

	constructor(settings: object)
	{
		super(settings);
		this._render();
	}

	_initFields(settings: object)
	{
		this.EvChanged = new Delegate()
		this._TextFieldSettings = {};
		this._UploadApiPath = '/athlete/creates3Link';
		super._initFields(settings);
	}

	_render()
	{
		this._initComponent('<div class="image-text-field"></div>');

		this._textField = new TextField({
			...this._TextFieldSettings,
			ParentNode: this._root,
			EvChanged: () => this.EvChanged.fire(this)
		})

		this._uploadButton = new FileUpload({
			ParentNode: this._root,
			RK: 'SelectFile',
			Accept: 'image/*',
			EvFileChanged: () => this.fileSelected()
		})

		if (this._ParentNode)
			this.putToNode(this._ParentNode);
	}

	async fileSelected()
	{
		let file = this._uploadButton.getFile();
		if (!file)
			return;
		try
		{
			const prepared = await prepareImageForUpload(file);

			let data = await post(this._UploadApiPath, {
				id: 0,
				name: prepared.name
			}) as {
				uploadUrl: string,
				getUrl: string
			}

			if (!data?.uploadUrl || !data?.getUrl)
			{
				NP.showMessage({Content: 'Upload failed: empty S3 link'});
				return;
			}

			const response = await fetch(data.uploadUrl, {
				method: 'PUT',
				body: prepared.blob,
			});

			if (!response.ok)
			{
				NP.showMessage({Content: 'Upload failed: ' + response.status});
				return;
			}

			await response.text();

			this._textField.setValue(data.getUrl)

			this.EvChanged.fire(this)
		}
		catch (e)
		{
			NP.showMessage({Content: 'Upload failed'});
		}
	}

	setValue(value: string)
	{
		this._textField.setValue(value)
	}

	getValue()
	{
		return this._textField.getValue();
	}
}
